"use client";

import { useEffect, useRef } from "react";

/**
 * Reserves the site header's height for as long as it is pinned.
 *
 * `public/main/js/main.js` pins `.sticky-wrapper` by switching it to
 * `position: fixed` once scrollY passes 500. That takes the header out of
 * normal flow, so the entire page below it jumps up by the header's height —
 * a visible jolt on every page, and it makes any scroll target measured on one
 * side of the threshold wrong on the other.
 *
 * This element sits immediately after the header and grows to exactly that
 * height while pinned, so the document keeps the same layout either way.
 */
export default function StickyHeaderSpacer() {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const spacer = ref.current;
    const header = spacer?.previousElementSibling;
    if (!spacer || !(header instanceof HTMLElement)) return;

    const sync = () => {
      // `offsetHeight` stays accurate while pinned — being fixed changes the
      // header's flow participation, not the size of its own box.
      const height = header.classList.contains("sticky")
        ? `${header.offsetHeight}px`
        : "0px";
      if (spacer.style.height !== height) spacer.style.height = height;
    };

    sync();

    // The theme toggles the class from jQuery, outside React's knowledge.
    const classObserver = new MutationObserver(sync);
    classObserver.observe(header, {
      attributes: true,
      attributeFilter: ["class"],
    });

    // The header's height changes across breakpoints and as its content loads.
    const sizeObserver = new ResizeObserver(sync);
    sizeObserver.observe(header);

    return () => {
      classObserver.disconnect();
      sizeObserver.disconnect();
    };
  }, []);

  return <div ref={ref} aria-hidden="true" style={{ height: 0 }} />;
}
